'use client'

import Modal from '@/components/Modal/Modal'
import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { useGraphicPreview } from '@/lib/features/graphic/GraphicPreviewContext'
import { cn } from '@/lib/utils'
import { useMemo, useState } from 'react'
import { useGraphicPreviewActions } from '../../lib/features/graphic/GraphicPreviewActionsContext'

export default function GraphicsToolbar() {
  const { graphic, hasChanges } = useGraphic()
  const { scale, setScale } = useGraphicPreview()
  const { onSave, onDiscard } = useGraphicPreviewActions()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Check if we're in development mode for debug features
  const isDev = process.env.NODE_ENV === 'development'

  const handleDiscard = () => {
    // Check if there are unsaved changes
    if (hasChanges()) {
      setShowConfirmDialog(true)
    } else {
      // No changes, proceed directly
      performDiscard()
    }
  }

  const performDiscard = () => {
    onDiscard()
  }

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false)
    performDiscard()
  }

  const handleCancelDiscard = () => {
    setShowConfirmDialog(false)
  }

  // Add scale control functions
  const decreaseScale = () => {
    setScale(Math.max(0.2, scale - 0.2))
  }

  const increaseScale = () => {
    setScale(Math.min(2.0, scale + 0.2))
  }

  const handleSave = () => {
    onSave()
  }

  const title = useMemo(() => {
    if (graphic?.config.type === 'image') {
      return `Image ${graphic.config.options.width}x${graphic.config.options.height}`
    } else if (graphic?.config.type === 'slide-deck') {
      return `Slide Deck`
    } else if (graphic?.config.type === 'document') {
      return `Document`
    }
    return `Graphic`
  }, [graphic])

  const handleOpenHtml = () => {
    if (!graphic?.html) {
      console.warn('No HTML content available')
      return
    }

    // Create a new tab with the HTML content
    const newWindow = window.open('', '_blank')
    if (newWindow) {
      newWindow.document.write(graphic.html)
      newWindow.document.close()
    }
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-gray-200 bg-white">
        {/* Save and Discard buttons */}
        <div className="flex items-center gap-2 p-2">
          <button
            onClick={handleSave}
            className="ml-2 inline-flex items-center justify-center rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Save
          </button>
          <button
            onClick={handleDiscard}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Discard
          </button>
          {/* Debug-only Open HTML button */}
          {isDev && graphic?.html && (
            <button
              onClick={handleOpenHtml}
              className="inline-flex items-center justify-center rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Open HTML
            </button>
          )}
        </div>

        {/* Center title */}
        <div className="flex-1 text-center">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        </div>

        {/* Scale controls */}
        <div className="mr-2 flex items-center gap-3 rounded p-2 text-xs">
          <span className="font-medium text-gray-700">
            <strong>Scale:</strong> {(scale * 100).toFixed(0)}%
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={decreaseScale}
              disabled={scale <= 0.2}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                scale <= 0.2
                  ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              -
            </button>
            <button
              onClick={increaseScale}
              disabled={scale >= 2.0}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                scale >= 2.0
                  ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Modal isOpen={showConfirmDialog} onClose={handleCancelDiscard}>
        <div className="relative flex w-[450px] max-w-[95vw] flex-col items-center gap-8 rounded-3xl border border-white/20 bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d] p-8 shadow-2xl backdrop-blur-lg">
          <div className="relative flex w-full flex-col items-center gap-6">
            {/* Header */}
            <div className="text-center">
              <h2 className="mb-3 text-2xl font-bold text-white">Confirm Discard</h2>
              <p className="text-lg text-white/70">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex w-full gap-4">
              <button
                onClick={handleCancelDiscard}
                className="flex-1 rounded-full border border-white/20 bg-white/10 px-4 py-3 font-medium text-white transition-all duration-200 hover:border-white/30 hover:bg-white/20"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDiscard}
                className="flex-1 rounded-full bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:from-red-700 hover:to-red-800 hover:shadow-red-500/25"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
